import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Icons } from '../Icons';
import { EarningsAnalytics } from '../charts/EarningsAnalytics';
import { ErrorBoundary } from '../ErrorBoundary';

const EASE    = [0.22, 1, 0.36, 1];
const SANS    = '"Geist", "Inter", system-ui, sans-serif';
const MONO    = '"Geist Mono", ui-monospace, monospace';
const BRAND   = '#5A38FD';
const BRAND_2 = '#7F00FF';
const BRAND_L = '#a78bfa';
const GRAIN_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`;

const DEAL_TYPES = ['Brand Partnership', 'Social Content', 'Event Appearance', 'Product Endorsement', 'Coaching', 'Other'];

// Purple-family category colors — no decorative multi-color per discipline rule
const TYPE_META = {
  'Brand Partnership':   { color: '#a78bfa', bg: 'rgba(167,139,250,0.10)' },
  'Social Content':      { color: '#818cf8', bg: 'rgba(129,140,248,0.10)' },
  'Event Appearance':    { color: '#c4b5fd', bg: 'rgba(196,181,253,0.10)' },
  'Product Endorsement': { color: '#9333ea', bg: 'rgba(147,51,234,0.10)'  },
  'Coaching':            { color: '#7c6cfc', bg: 'rgba(124,108,252,0.10)' },
  'Other':               { color: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.05)' },
};

const fmt     = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (ds) => new Date(ds).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const initDeal = { company_name: '', deal_value: '', deal_date: '', description: '', deal_type: 'Brand Partnership' };

// ─── Shared input style ───────────────────────────────────────────────────────
const INPUT = {
  width: '100%', boxSizing: 'border-box',
  padding: '10px 13px', borderRadius: 9,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: '#fff', fontSize: '0.875rem',
  outline: 'none', fontFamily: SANS,
  transition: 'border-color 160ms, background 160ms, box-shadow 160ms',
};

const focusInput  = (e) => { e.target.style.borderColor = 'rgba(90,56,253,0.55)'; e.target.style.background = 'rgba(90,56,253,0.06)'; e.target.style.boxShadow = '0 0 0 3px rgba(90,56,253,0.12)'; };
const blurInput   = (e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.04)'; e.target.style.boxShadow = 'none'; };

// ─── Buttons ──────────────────────────────────────────────────────────────────
const BtnPrimary = ({ children, onClick, disabled, type = 'button', style = {} }) => (
  <motion.button whileTap={{ scale: 0.97 }} type={type} onClick={onClick} disabled={disabled}
    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 10, border: 'none',
      background: disabled ? 'rgba(90,56,253,0.35)' : `linear-gradient(135deg, ${BRAND}, ${BRAND_2})`,
      color: '#fff', fontWeight: 700, fontSize: '0.875rem', cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: SANS, boxShadow: disabled ? 'none' : '0 4px 14px rgba(90,56,253,0.35)',
      whiteSpace: 'nowrap', ...style }}>
    {children}
  </motion.button>
);

const BtnGhost = ({ children, onClick, danger, type = 'button', style = {} }) => (
  <motion.button whileTap={{ scale: 0.97 }} type={type} onClick={onClick}
    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 10,
      background: danger ? 'rgba(248,113,113,0.07)' : 'rgba(255,255,255,0.04)',
      border: `1px solid ${danger ? 'rgba(248,113,113,0.22)' : 'rgba(255,255,255,0.1)'}`,
      color: danger ? '#f87171' : 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: '0.875rem',
      cursor: 'pointer', fontFamily: SANS, whiteSpace: 'nowrap', ...style }}>
    {children}
  </motion.button>
);

// ─── Company avatar ───────────────────────────────────────────────────────────
function CompanyAvatar({ name, dealType }) {
  const meta = TYPE_META[dealType] || TYPE_META.Other;
  return (
    <div style={{ width: 44, height: 44, borderRadius: 13, flexShrink: 0,
      background: meta.bg, border: `1px solid ${meta.color}30`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '1.1rem', fontWeight: 800, color: meta.color, fontFamily: SANS }}>
      {(name || '?')[0].toUpperCase()}
    </div>
  );
}

// ─── Sheet modal ──────────────────────────────────────────────────────────────
function Sheet({ isOpen, onClose, title, subtitle, children }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>{`
            @media (max-width: 640px) {
              .nv-sheet-v3 {
                position: fixed !important; bottom: 0 !important; left: 0 !important;
                right: 0 !important; top: auto !important;
                border-radius: 20px 20px 0 0 !important;
                max-width: 100% !important; max-height: 92vh !important;
              }
            }
            @keyframes et-spin { to { transform: rotate(360deg); } }
          `}</style>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            onClick={onClose} />
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto', pointerEvents: 'none' }}>
            <div style={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
              <motion.div initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 16 }} transition={{ duration: 0.25, ease: EASE }}
                className="nv-sheet-v3"
                onClick={e => e.stopPropagation()}
                style={{ pointerEvents: 'auto', background: '#0F0F17', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20,
                  width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto',
                  boxShadow: '0 24px 80px rgba(0,0,0,0.85)', position: 'relative', overflow: 'hidden', fontFamily: SANS }}>
                {/* Top hairline */}
                <div style={{ position: 'absolute', top: 0, left: '15%', right: '15%', height: 1, background: `linear-gradient(90deg, transparent, rgba(90,56,253,0.5), transparent)` }} />
                {/* Grain */}
                <div style={{ position: 'absolute', inset: 0, opacity: 0.035, mixBlendMode: 'overlay', backgroundImage: GRAIN_SVG, backgroundSize: '120px 120px', pointerEvents: 'none', borderRadius: 20 }} />

                <div style={{ position: 'relative', zIndex: 1, overflowY: 'auto', maxHeight: '90vh' }}>
                  {/* Handle */}
                  <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12 }}>
                    <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.1)' }} />
                  </div>
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '14px 20px 10px' }}>
                    <div>
                      <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', margin: 0 }}>{title}</h3>
                      {subtitle && <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.3)', margin: '3px 0 0' }}>{subtitle}</p>}
                    </div>
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}>×</button>
                  </div>
                  <div style={{ padding: '0 20px 24px' }}>{children}</div>
                </div>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

// ─── Deal form ────────────────────────────────────────────────────────────────
function DealForm({ deal, setDeal, onSubmit, submitting, onCancel, submitLabel = 'Save' }) {
  return (
    <form onSubmit={onSubmit}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 13, marginTop: 4 }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Brand / Company *</label>
          <input type="text" required value={deal.company_name}
            onChange={e => setDeal(d => ({ ...d, company_name: e.target.value }))}
            style={INPUT} placeholder="Nike, Gatorade, Local Business…"
            onFocus={focusInput} onBlur={blurInput} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Amount ($) *</label>
            <input type="number" min="0" step="0.01" required value={deal.deal_value}
              onChange={e => setDeal(d => ({ ...d, deal_value: e.target.value }))}
              style={INPUT} placeholder="0"
              onFocus={focusInput} onBlur={blurInput} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Date *</label>
            <input type="date" required value={(deal.deal_date || '').slice(0, 10)}
              onChange={e => setDeal(d => ({ ...d, deal_date: e.target.value }))}
              style={INPUT}
              onFocus={focusInput} onBlur={blurInput} />
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Category</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {DEAL_TYPES.map(t => {
              const meta = TYPE_META[t];
              const active = deal.deal_type === t;
              return (
                <button key={t} type="button" onClick={() => setDeal(d => ({ ...d, deal_type: t }))}
                  style={{ padding: '5px 12px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', transition: 'all 150ms',
                    border: active ? `1px solid ${meta.color}55` : '1px solid rgba(255,255,255,0.1)',
                    background: active ? meta.bg : 'transparent',
                    color: active ? meta.color : 'rgba(255,255,255,0.35)',
                    fontFamily: SANS }}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Notes</label>
          <textarea value={deal.description || ''} onChange={e => setDeal(d => ({ ...d, description: e.target.value }))}
            style={{ ...INPUT, resize: 'none' }} rows="3" placeholder="Brief description of the deal…"
            onFocus={focusInput} onBlur={blurInput} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        <BtnGhost type="button" onClick={onCancel} style={{ flex: 1, justifyContent: 'center' }}>Cancel</BtnGhost>
        <BtnPrimary type="submit" disabled={submitting} style={{ flex: 2, justifyContent: 'center' }}>
          {submitting
            ? <><div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'et-spin 0.8s linear infinite' }} />Saving…</>
            : submitLabel}
        </BtnPrimary>
      </div>
    </form>
  );
}

// ─── Attach Contract Sheet ────────────────────────────────────────────────────
function AttachContractSheet({ deal, onClose, onAttached, apiCall, setActiveComponent }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [attaching, setAttaching] = useState(null);

  useEffect(() => {
    const load = async () => {
      try { const res = await apiCall('/api/contracts/history'); setContracts(res?.contracts || []); }
      catch {} finally { setLoading(false); }
    };
    load();
  }, [apiCall]);

  const safetyColor = (s) => s >= 80 ? '#4ade80' : s >= 55 ? '#fbbf24' : '#f87171';

  const handleAttach = async (contract) => {
    setAttaching(contract.contract_id);
    try {
      const dealId = deal._id || deal.deal_id;
      await apiCall(`/api/nil-deals/${dealId}`, {
        method: 'PUT',
        body: JSON.stringify({ audit_status: 'has_contract', contract_id: contract.contract_id, contract_title: contract.deal_title || contract.filename || 'Contract' }),
      });
      onAttached(dealId, contract);
    } catch {}
    setAttaching(null);
  };

  return (
    <Sheet isOpen onClose={onClose} title="Attach Contract" subtitle={deal.company_name}>
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', gap: 8, color: 'rgba(255,255,255,0.3)', fontFamily: SANS }}>
          <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.1)', borderTopColor: BRAND_L, borderRadius: '50%', animation: 'et-spin 0.8s linear infinite' }} />
          Loading contracts…
        </div>
      ) : contracts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.875rem', marginBottom: '1rem', fontFamily: SANS }}>
            No analyzed contracts yet. Upload one to get started.
          </p>
          <BtnPrimary onClick={() => { onClose(); setActiveComponent?.('analyzer'); }}>
            Analyze a Contract →
          </BtnPrimary>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ margin: '0 0 6px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)', fontFamily: SANS }}>
            Select a contract to link to this deal.
          </p>
          {contracts.map(c => (
            <div key={c.contract_id}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12,
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: SANS }}>
                  {c.deal_title || c.filename || 'Unnamed Contract'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3 }}>
                  <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.28)', fontFamily: MONO }}>
                    {c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                  </span>
                  {c.safety_score != null && (
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '1px 7px', borderRadius: 999,
                      color: safetyColor(c.safety_score), background: `${safetyColor(c.safety_score)}18`,
                      border: `1px solid ${safetyColor(c.safety_score)}35`, fontFamily: MONO }}>
                      {c.safety_score}/100
                    </span>
                  )}
                </div>
              </div>
              <BtnPrimary onClick={() => handleAttach(c)} disabled={attaching === c.contract_id}
                style={{ padding: '6px 14px', fontSize: '0.78rem' }}>
                {attaching === c.contract_id
                  ? <><div style={{ width: 10, height: 10, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'et-spin 0.8s linear infinite' }} />Linking…</>
                  : 'Attach'}
              </BtnPrimary>
            </div>
          ))}
          <button onClick={() => { onClose(); setActiveComponent?.('analyzer'); }}
            style={{ marginTop: 4, padding: '10px', borderRadius: 10, fontSize: '0.82rem', fontWeight: 600,
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.35)', cursor: 'pointer', width: '100%', fontFamily: SANS }}>
            + Upload & analyze a new contract
          </button>
        </div>
      )}
    </Sheet>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const NILEarningsTracker = ({ setSuccess, setError, setActiveComponent }) => {
  const { apiCall } = useAuth();
  const [loading, setLoading]             = useState(true);
  const [deals, setDeals]                 = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [monthlyEarnings, setMonthlyEarnings] = useState(0);
  const [activeFilter, setActiveFilter]   = useState('All');
  const [showAddSheet, setShowAddSheet]   = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingDeal, setEditingDeal]     = useState(null);
  const [dealToDelete, setDealToDelete]   = useState(null);
  const [newDeal, setNewDeal]             = useState(initDeal);
  const [submitting, setSubmitting]       = useState(false);
  const [deleting, setDeleting]           = useState(false);
  const [attachDeal, setAttachDeal]       = useState(null);

  const handleAttached = (dealId, contract) => {
    setDeals(prev => prev.map(d =>
      (d._id || d.deal_id) === dealId
        ? { ...d, audit_status: 'has_contract', contract_id: contract.contract_id, contract_title: contract.deal_title || contract.filename }
        : d
    ));
    setAttachDeal(null);
    setSuccess('Contract linked to deal');
    window.dispatchEvent(new CustomEvent('earnings:changed', {}));
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await apiCall('/api/earnings');
      if (res?.success) {
        setDeals(res.deals || []);
        setTotalEarnings(res.totalEarnings || 0);
        setMonthlyEarnings(res.thisMonth || 0);
      }
    } catch { setError('Failed to load earnings data'); }
    finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        company_name: newDeal.company_name.trim(),
        deal_value:   parseFloat(newDeal.deal_value),
        deal_date:    newDeal.deal_date,
        description:  (newDeal.description || '').trim(),
        deal_type:    newDeal.deal_type,
      };
      const res = await apiCall('/api/earnings/add-deal', { method: 'POST', body: JSON.stringify(payload) });
      if (res?.success || res?.message) {
        setDeals(prev => [{ ...payload, deal_id: res.deal_id || Date.now().toString(), status: 'active', created_at: new Date().toISOString() }, ...prev]);
        setTotalEarnings(prev => prev + payload.deal_value);
        const d = new Date(payload.deal_date), now = new Date();
        if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) setMonthlyEarnings(prev => prev + payload.deal_value);
        window.dispatchEvent(new CustomEvent('earnings:changed', { detail: { total: totalEarnings + payload.deal_value } }));
        setNewDeal(initDeal);
        setShowAddSheet(false);
        setSuccess('Deal added!');
      } else { setError(res?.message || 'Failed to add deal'); }
    } catch { setError('Failed to add deal. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const id = editingDeal._id || editingDeal.deal_id;
      const payload = { company_name: editingDeal.company_name, deal_value: parseFloat(editingDeal.deal_value), deal_date: editingDeal.deal_date, description: editingDeal.description, deal_type: editingDeal.deal_type };
      const res = await apiCall(`/api/nil-deals/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
      if (res?.deal) {
        setDeals(prev => prev.map(d => ((d._id === id) || (d.deal_id === id)) ? { ...d, ...payload } : d));
        setShowEditSheet(false);
        setSuccess('Deal updated!');
        try {
          const r2 = await apiCall('/api/earnings');
          if (r2?.success) window.dispatchEvent(new CustomEvent('earnings:changed', { detail: { total: r2.totalEarnings ?? 0 } }));
        } catch {}
      } else { setError('Failed to update deal'); }
    } catch (err) { setError(err.message || 'Failed to update deal'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!dealToDelete) return;
    setDeleting(true);
    try {
      const res = await apiCall(`/api/nil-deals/${dealToDelete.id}`, { method: 'DELETE' });
      if (res?.message) {
        setSuccess('Deal deleted');
        await loadData();
        try {
          const r2 = await apiCall('/api/earnings');
          if (r2?.success) window.dispatchEvent(new CustomEvent('earnings:changed', { detail: { total: r2.totalEarnings ?? 0 } }));
        } catch {}
      }
    } catch { setError('Failed to delete deal.'); }
    finally { setDeleting(false); setShowDeleteModal(false); setDealToDelete(null); }
  };

  const avgDeal  = deals.length > 0 ? totalEarnings / deals.length : 0;
  const filtered = activeFilter === 'All' ? deals : deals.filter(d => (d.deal_type || 'Other') === activeFilter);
  const allTypes = ['All', ...Array.from(new Set(deals.map(d => d.deal_type || 'Other')))];

  // ── Skeleton ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 12, fontFamily: SANS }}>
        <style>{`@keyframes skWave { 0%,100%{opacity:0.4}50%{opacity:0.7} }`}</style>
        {[180, 80, 200, 72, 72, 72].map((h, i) => (
          <div key={i} style={{ height: h, borderRadius: 18, background: 'rgba(255,255,255,0.05)', animation: `skWave 1.6s ease-in-out ${i * 0.12}s infinite` }} />
        ))}
      </div>
    );
  }

  return (
    <>
      <style>{`@keyframes et-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px 80px', display: 'flex', flexDirection: 'column', gap: 12, fontFamily: SANS }}>

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: EASE }}
          style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 20,
            padding: '24px 20px', position: 'relative', overflow: 'hidden' }}>
          {/* Grain */}
          <div style={{ position: 'absolute', inset: 0, opacity: 0.04, mixBlendMode: 'overlay', backgroundImage: GRAIN_SVG, backgroundSize: '120px 120px', pointerEvents: 'none', borderRadius: 20 }} />
          {/* Ambient glow */}
          <div style={{ position: 'absolute', top: -60, left: '50%', transform: 'translateX(-50%)', width: 320, height: 200, background: 'radial-gradient(ellipse, rgba(90,56,253,0.16) 0%, transparent 70%)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Top row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: BRAND_L, boxShadow: `0 0 6px ${BRAND_L}` }} />
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: MONO }}>NIL Earnings</span>
              </div>
              <BtnPrimary onClick={() => setShowAddSheet(true)} style={{ padding: '8px 14px', fontSize: '0.82rem' }}>
                <Icons.Plus style={{ width: 14, height: 14 }} />
                Add Deal
              </BtnPrimary>
            </div>

            {/* Total earnings — green because it's an earnings amount (rule-correct) */}
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
                style={{ fontSize: 'clamp(2.5rem, 8vw, 3.75rem)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1,
                  background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontFamily: MONO }}>
                {fmt(totalEarnings)}
              </motion.div>
              <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.28)', marginTop: 6, fontFamily: MONO, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Total NIL Earnings</p>
            </div>

            {/* Stat row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
              {[
                { label: 'This month', value: fmt(monthlyEarnings), color: '#4ade80' },
                { label: 'Total deals', value: String(deals.length), color: '#fff' },
                { label: 'Avg / deal', value: fmt(avgDeal), color: BRAND_L },
              ].map((s, i) => (
                <React.Fragment key={s.label}>
                  {i > 0 && <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.08)', margin: '0 20px' }} />}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: s.color, letterSpacing: '-0.02em', fontFamily: MONO }}>{s.value}</div>
                    <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.25)', marginTop: 2, fontFamily: MONO }}>{s.label}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ── Analytics chart ───────────────────────────────────────────────── */}
        {deals.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE, delay: 0.1 }}
            style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 20, padding: '20px', overflow: 'hidden' }}>
            <ErrorBoundary title="Analytics unavailable" description="There was a problem loading the earnings charts.">
              <EarningsAnalytics deals={deals} totalEarnings={totalEarnings} monthlyEarnings={monthlyEarnings} />
            </ErrorBoundary>
          </motion.div>
        )}

        {/* ── Category breakdown ────────────────────────────────────────────── */}
        {deals.length > 0 && (() => {
          const map = deals.reduce((acc, d) => { const t = d.deal_type || 'Other'; acc[t] = (acc[t] || 0) + (d.deal_value || 0); return acc; }, {});
          return (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE, delay: 0.15 }}
              style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 20, padding: '20px' }}>
              <p style={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.25)', margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: MONO }}>By Category</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {Object.entries(map).sort((a, b) => b[1] - a[1]).map(([type, val]) => {
                  const pct  = totalEarnings > 0 ? (val / totalEarnings) * 100 : 0;
                  const meta = TYPE_META[type] || TYPE_META.Other;
                  return (
                    <div key={type}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
                          <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', fontWeight: 500 }}>{type}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', fontFamily: MONO }}>{fmt(val)}</span>
                          <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)', fontFamily: MONO, minWidth: 32, textAlign: 'right' }}>{pct.toFixed(0)}%</span>
                        </div>
                      </div>
                      <div style={{ height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                        <motion.div style={{ height: '100%', borderRadius: 999, background: meta.color }}
                          initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, ease: EASE }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          );
        })()}

        {/* ── Deals feed ────────────────────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE, delay: 0.2 }}>
          {/* Filter chips */}
          {deals.length > 0 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2, marginBottom: 12, scrollbarWidth: 'none' }}>
              {allTypes.map(type => {
                const meta = type === 'All' ? null : (TYPE_META[type] || TYPE_META.Other);
                const active = activeFilter === type;
                return (
                  <button key={type} onClick={() => setActiveFilter(type)}
                    style={{ flexShrink: 0, padding: '5px 14px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600,
                      border: active ? `1px solid ${meta ? meta.color + '55' : 'rgba(90,56,253,0.55)'}` : '1px solid rgba(255,255,255,0.09)',
                      background: active ? (meta ? meta.bg : 'rgba(90,56,253,0.12)') : 'transparent',
                      color: active ? (meta ? meta.color : BRAND_L) : 'rgba(255,255,255,0.3)',
                      cursor: 'pointer', transition: 'all 150ms', fontFamily: SANS }}>
                    {type}
                  </button>
                );
              })}
            </div>
          )}

          {/* Deal cards */}
          {filtered.length === 0 ? (
            <div style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 20, padding: '48px 24px', textAlign: 'center' }}>
              <div style={{ width: 52, height: 52, borderRadius: 16, background: 'rgba(90,56,253,0.1)', border: '1px solid rgba(90,56,253,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <Icons.Earnings style={{ width: 22, height: 22, color: BRAND_L }} />
              </div>
              <p style={{ color: '#fff', fontWeight: 700, fontSize: '1rem', margin: '0 0 6px' }}>
                {deals.length === 0 ? 'No deals yet' : `No ${activeFilter} deals`}
              </p>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem', margin: '0 0 18px' }}>
                {deals.length === 0 ? 'Add your first NIL deal to start tracking' : 'Try a different filter'}
              </p>
              {deals.length === 0 && (
                <BtnPrimary onClick={() => setShowAddSheet(true)}>Add Your First Deal</BtnPrimary>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map((deal, i) => {
                const meta = TYPE_META[deal.deal_type] || TYPE_META.Other;
                const id   = deal.deal_id || deal._id;
                return (
                  <motion.div key={id || i}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: EASE, delay: i * 0.04 }}
                    onClick={() => { setEditingDeal({ ...(deal._id ? deal : { ...deal, _id: deal.deal_id }) }); setShowEditSheet(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14,
                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: 16, padding: '14px 16px', cursor: 'pointer', transition: 'background 150ms, border-color 150ms' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}>
                    <CompanyAvatar name={deal.company_name} dealType={deal.deal_type} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                        <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {deal.company_name}
                        </div>
                        {deal.audit_status === 'missing_contract' && (
                          <button onClick={e => { e.stopPropagation(); setAttachDeal(deal); }}
                            title="No contract on file — click to attach"
                            style={{ flexShrink: 0, fontSize: '0.62rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                              background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
                              color: '#f59e0b', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: SANS }}>
                            ⚠ Attach Contract
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 999, background: meta.bg, color: meta.color, fontWeight: 600, fontFamily: SANS }}>
                          {deal.deal_type || 'Other'}
                        </span>
                        <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)', fontFamily: MONO }}>{fmtDate(deal.deal_date)}</span>
                      </div>
                    </div>

                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {/* Green for earnings amount — rule-correct */}
                      <div style={{ fontSize: '1rem', fontWeight: 800, color: '#4ade80', letterSpacing: '-0.02em', fontFamily: MONO }}>
                        {fmt(deal.deal_value)}
                      </div>
                      <button onClick={e => { e.stopPropagation(); setDealToDelete({ id: deal._id || deal.deal_id, info: deal }); setShowDeleteModal(true); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(248,113,113,0.4)', padding: '3px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                        <Icons.Trash style={{ width: 12, height: 12 }} />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>

      {/* ── Attach contract sheet ─────────────────────────────────────────────── */}
      {attachDeal && (
        <AttachContractSheet deal={attachDeal} onClose={() => setAttachDeal(null)} onAttached={handleAttached} apiCall={apiCall} setActiveComponent={setActiveComponent} />
      )}

      {/* ── Add sheet ──────────────────────────────────────────────────────────── */}
      <Sheet isOpen={showAddSheet} onClose={() => setShowAddSheet(false)} title="New Deal" subtitle="Track your NIL earnings">
        <DealForm deal={newDeal} setDeal={setNewDeal} onSubmit={handleAdd} submitting={submitting} onCancel={() => setShowAddSheet(false)} submitLabel="Add Deal" />
      </Sheet>

      {/* ── Edit sheet ─────────────────────────────────────────────────────────── */}
      <Sheet isOpen={showEditSheet && !!editingDeal} onClose={() => setShowEditSheet(false)} title="Edit Deal" subtitle="Update deal information">
        {editingDeal && (
          <DealForm deal={editingDeal} setDeal={setEditingDeal} onSubmit={handleEdit} submitting={submitting} onCancel={() => setShowEditSheet(false)} submitLabel="Save Changes" />
        )}
      </Sheet>

      {/* ── Delete confirm ─────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showDeleteModal && dealToDelete && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
              onClick={() => { setShowDeleteModal(false); setDealToDelete(null); }} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.22, ease: EASE }}
              style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', pointerEvents: 'none' }}>
              <div onClick={e => e.stopPropagation()} style={{ pointerEvents: 'all', background: '#0F0F17', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 20, padding: '28px 24px', width: '100%', maxWidth: 360, textAlign: 'center', boxShadow: '0 24px 60px rgba(0,0,0,0.85)', fontFamily: SANS, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: '20%', right: '20%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(239,68,68,0.4), transparent)' }} />
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                  <Icons.Trash style={{ width: 20, height: 20, color: '#f87171' }} />
                </div>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', margin: '0 0 6px' }}>Delete deal?</h3>
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)', margin: '0 0 6px' }}>
                  {dealToDelete.info?.company_name} · <span style={{ color: '#4ade80', fontWeight: 700, fontFamily: MONO }}>{fmt(dealToDelete.info?.deal_value)}</span>
                </p>
                <p style={{ fontSize: '0.75rem', color: 'rgba(248,113,113,0.6)', margin: '0 0 20px' }}>This cannot be undone.</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <BtnGhost onClick={() => { setShowDeleteModal(false); setDealToDelete(null); }} style={{ flex: 1, justifyContent: 'center' }}>Cancel</BtnGhost>
                  <motion.button whileTap={{ scale: 0.97 }} onClick={handleDelete} disabled={deleting}
                    style={{ flex: 1, padding: '10px 0', background: deleting ? 'rgba(220,38,38,0.4)' : 'linear-gradient(135deg, #dc2626, #b91c1c)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontFamily: SANS }}>
                    {deleting ? 'Deleting…' : 'Delete'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NILEarningsTracker;
